import { useEffect, useState } from 'react';
import { Dialog } from '@aramo/fe-foundation';

import { DEFAULT_WITHDRAW_NOTICE_DAYS, MAX_WITHDRAW_NOTICE_DAYS, type TemplateStudioCopy } from './data-source';
import { formatDate } from './format-date';
import { ConfirmDialog, FieldText, FieldTextArea, useReturnFocus } from './primitives';
import type { DiffStats } from './diff';

// PLATFORM-UX-1 F-9 (handoff §10.7) — the Studio's confirm dialogs, on the Studio's
// ConfirmDialog (primitives.tsx) (from → to Change box, consequence, fields, error; focus trap, Esc,
// focus return): Publish vN, Withdraw, Cancel withdrawal, Restore into draft — and the
// keyboard shortcuts sheet (§10.8).

export interface PublishDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly name: string;
  readonly next: number;
  readonly current: number | null;
  readonly stats: DiffStats;
  readonly variables: number;
  readonly warnings: number;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onPublish: () => void;
  /** The context's approve copy (TEMPLATE-STUDIO-1 S-0). */
  readonly copy: TemplateStudioCopy['approveDialog'];
  /** TEMPLATE-STUDIO-1 S-1 — the context's inactive-variable warning line. */
  readonly warningsText?: (n: number) => string;
}

export function publishConsequence(next: number, current: number | null): string {
  return `v${next} becomes the platform default for every tenant that uses this template. Documents already sent are not changed.${
    current !== null ? ` v${current} is marked Replaced and stays viewable.` : ''
  }`;
}

export function PublishDialog(p: PublishDialogProps) {
  const tiles: ReadonlyArray<[string, number]> = [
    ['Added', p.stats.added],
    ['Changed', p.stats.changed],
    ['Removed', p.stats.removed],
    ['Variables', p.variables],
  ];
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title={p.copy.title(p.next, p.name)}
      from={p.copy.from(p.current)}
      to={p.copy.to(p.next)}
      consequence={p.copy.consequence(p.next, p.current)}
      tone="privileged"
      commitLabel={p.copy.commit(p.next)}
      busy={p.busy}
      error={p.error}
      onCommit={p.onPublish}
    >
      <div className="pws-tiles">
        {tiles.map(([l, c]) => (
          <div key={l} className="pws-tile">
            <div className="pws-tile__c">{c}</div>
            <div className="pws-tile__l">{l}</div>
          </div>
        ))}
      </div>
      {p.warnings > 0 ? (
        <div className="ts-alert ts-alert--warn pws-dlg-warn">
          {p.warningsText !== undefined ? (
            p.warningsText(p.warnings)
          ) : (
            <>
              {p.warnings} warning{p.warnings > 1 ? 's' : ''} (inactive variables) will publish as is.
            </>
          )}
        </div>
      ) : null}
    </ConfirmDialog>
  );
}

/** Notice days valid: a whole number 0–365. */
export function noticeDaysValid(raw: string): boolean {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= MAX_WITHDRAW_NOTICE_DAYS;
}

/** The effective date line under the withdraw fields. */
export function effectiveLine(raw: string, now: Date = new Date()): string {
  if (!noticeDaysValid(raw)) return `Enter 0–${MAX_WITHDRAW_NOTICE_DAYS} days.`;
  const d = Number(raw);
  return `Takes effect ${formatDate(new Date(now.getTime() + d * 86_400_000))}${d === 0 ? ' (immediately)' : ''}`;
}

export interface WithdrawDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly name: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onWithdraw: (days: number, reason: string) => void;
}

export function WithdrawDialog(p: WithdrawDialogProps) {
  const [days, setDays] = useState(String(DEFAULT_WITHDRAW_NOTICE_DAYS));
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (p.open) {
      setDays(String(DEFAULT_WITHDRAW_NOTICE_DAYS));
      setReason('');
    }
  }, [p.open]);
  const ok = noticeDaysValid(days) && reason.trim() !== '';
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title={`Withdraw ${p.name}`}
      consequence="Tenants are told this default is being withdrawn. After the notice period it can’t be used for new documents. Documents already sent are unaffected."
      tone="privileged"
      commitLabel="Withdraw template"
      canCommit={ok}
      busy={p.busy}
      error={p.error}
      onCommit={() => p.onWithdraw(Number(days), reason.trim())}
    >
      <FieldText label="Notice period (days)" required type="number" value={days} onChange={setDays} helper="0–365. 0 withdraws immediately." />
      <FieldTextArea label="Reason" required value={reason} onChange={setReason} max={500} helper="Shown to tenant admins with the notice." />
      <div className="pws-dlg-live" aria-live="polite">
        {effectiveLine(days)}
      </div>
    </ConfirmDialog>
  );
}

export interface SimpleConfirmProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly busy?: boolean;
  readonly error?: string | null;
  readonly onConfirm: () => void;
}

export function CancelWithdrawalDialog(p: SimpleConfirmProps) {
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title="Cancel withdrawal"
      consequence="The template stays available to tenants. Tenant admins are told the withdrawal was cancelled."
      commitLabel="Cancel withdrawal"
      busy={p.busy}
      error={p.error}
      onCommit={p.onConfirm}
    />
  );
}

export function RestoreDialog(p: SimpleConfirmProps & { readonly version: number }) {
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title={`Restore v${p.version} into the draft`}
      consequence={`Replaces the current draft content with v${p.version}. Published versions are not changed. You can still compare or undo by restoring another version.`}
      commitLabel="Replace draft"
      onCommit={p.onConfirm}
    />
  );
}

/** TEMPLATE-STUDIO-1 S-1 — discard the open draft (organization; DELETE …/versions/:id). */
export function DiscardDialog(p: SimpleConfirmProps & { readonly version: number; readonly published?: boolean }) {
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title={`Discard draft v${p.version}?`}
      consequence={
        p.published === false
          ? 'The draft and any unsaved changes are deleted. The template stays, with no draft.'
          : 'The draft and any unsaved changes are deleted. The active version is not changed and stays in use.'
      }
      tone="privileged"
      commitLabel="Discard draft"
      busy={p.busy}
      error={p.error}
      onCommit={p.onConfirm}
    />
  );
}

/** TEMPLATE-PRODUCT-1 §3.4 — delete a template that was never published. */
export function DeleteTemplateDialog(p: SimpleConfirmProps & { readonly name: string }) {
  return (
    <ConfirmDialog
      open={p.open}
      onOpenChange={p.onOpenChange}
      title={`Delete ${p.name}?`}
      consequence="The template and its draft are deleted. It was never published, so no tenant has it. This can’t be undone."
      tone="privileged"
      commitLabel="Delete template"
      busy={p.busy}
      error={p.error}
      onCommit={p.onConfirm}
    />
  );
}

export const SHORTCUTS: ReadonlyArray<[string, string]> = [
  ['⌘ S', 'Save draft'],
  ['⌘ ⇧ P', 'Toggle preview'],
  ['⌘ B / I / U', 'Bold, italic, underline'],
  ['/', 'Block menu on an empty line'],
  ['{{', 'Insert a variable'],
  ['Enter', 'New paragraph'],
  ['⌥ ↑ / ⌥ ↓', 'Move block up or down'],
  ['Backspace', 'Delete an empty block'],
  ['⌘ Z / ⌘ ⇧ Z', 'Undo / redo'],
  ['⌘ \\', 'Collapse or expand the sidebar'],
  ['?', 'This sheet'],
  ['Esc', 'Close menus and dialogs'],
];

export function ShortcutsSheet({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  useReturnFocus(open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Keyboard shortcuts">
      <div className="pws-keys">
        {SHORTCUTS.map(([k, l]) => (
          <span key={k} className="pws-keys__row">
            <span className="pws-keys__k">{k}</span>
            <span>{l}</span>
          </span>
        ))}
      </div>
    </Dialog>
  );
}
